import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { ShieldCheck, AlertCircle, KeyRound, Copy, CheckCircle2 } from "lucide-react";

const FIELD = "mt-1.5 h-11 border-white/12 bg-white/5 text-white placeholder:text-slate-500 focus-visible:border-blue-400/70 focus-visible:ring-blue-500/20";

export default function RootSetup() {
  const [token, setTokenVal] = useState("");
  const [setup, setSetup] = useState(null);
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setTokenVal(t);
    if (t) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const begin = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/root/setup/begin", { token });
      setSetup(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const activate = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/root/setup/complete", { token, password, otp });
      setCodes(data.recovery_codes || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const copyCodes = () => {
    navigator.clipboard.writeText((codes || []).join("\n"));
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-[#0A1129] px-5 py-10">
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(900px 420px at 80% -10%, rgba(37,99,235,0.28), transparent 60%), radial-gradient(700px 420px at 10% 110%, rgba(6,182,212,0.16), transparent 55%)" }} />
      <div className="relative z-10 w-full max-w-md" data-testid="root-setup-page">
        <div className="mb-6 flex flex-col items-center text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/12 text-blue-300 ring-1 ring-inset ring-blue-400/30" style={{ boxShadow: "0 0 18px rgba(22,132,255,0.4)" }}><ShieldCheck size={24} /></span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-white">Aktivasi ROOT OWNER</h1>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-blue-300/80">Titanium EOS · Break-Glass Setup</p>
        </div>

        <div className="ti-dcard p-6 sm:p-8">
          {error && (
            <div data-testid="root-setup-error" className="mb-4 flex items-start gap-2 rounded-lg border border-rose-400/30 bg-rose-500/10 px-3 py-2.5 text-sm font-medium text-rose-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}

          {!token && <p className="text-sm text-slate-400">Link setup tidak valid. Buka halaman ini melalui secure setup link.</p>}

          {token && !setup && !codes && (
            <div className="space-y-4">
              <p className="text-sm text-slate-300">Link setup aman terverifikasi. Mulai untuk membuat QR MFA dan menetapkan password.</p>
              <Button data-testid="root-setup-begin-btn" onClick={begin} disabled={loading} className="h-11 w-full bg-blue-600 font-semibold text-white hover:bg-blue-500">{loading ? "Mohon tunggu…" : "Mulai Setup Aman"}</Button>
            </div>
          )}

          {setup && !codes && (
            <form onSubmit={activate} className="space-y-4">
              <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-center">
                <img src={setup.qr_data_url} alt="QR MFA" className="mx-auto h-44 w-44 rounded-lg bg-white p-2" data-testid="root-setup-qr" />
                <p className="mt-2 text-xs text-slate-400">Scan dengan Google / Microsoft Authenticator</p>
              </div>
              <div>
                <Label className="text-slate-300">Password baru (min 12 karakter)</Label>
                <PasswordInput data-testid="root-setup-password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={12} required className={FIELD} />
              </div>
              <div>
                <Label className="text-slate-300">Kode 6-digit dari authenticator</Label>
                <Input data-testid="root-setup-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} required placeholder="123456" className={FIELD} />
              </div>
              <Button type="submit" data-testid="root-setup-activate-btn" disabled={loading} className="h-11 w-full bg-blue-600 font-semibold text-white hover:bg-blue-500">{loading ? "Mengaktifkan…" : "Aktifkan ROOT OWNER"}</Button>
            </form>
          )}

          {codes && (
            <div className="space-y-4" data-testid="root-setup-codes">
              <div className="flex items-center gap-2 text-emerald-300"><CheckCircle2 size={20} /><span className="font-semibold">ROOT OWNER aktif!</span></div>
              <p className="text-sm text-slate-300">Simpan recovery codes ini sekarang. Kode tidak akan ditampilkan lagi.</p>
              <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm text-amber-100 select-all">
                  {codes.map((c) => <span key={c} className="rounded bg-black/20 px-2 py-1">{c}</span>)}
                </div>
              </div>
              <Button data-testid="root-copy-codes-btn" onClick={copyCodes} variant="outline" className="h-10 w-full border-white/15 bg-white/5 text-white hover:bg-white/10">{copied ? <><CheckCircle2 size={15} className="mr-1.5" />Tersalin</> : <><Copy size={15} className="mr-1.5" />Salin recovery codes</>}</Button>
              <a href="/login" data-testid="root-goto-login" className="flex h-11 w-full items-center justify-center gap-2 rounded-md bg-blue-600 font-semibold text-white hover:bg-blue-500"><KeyRound size={16} /> Lanjut ke Login</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
